import React, { useEffect, useState, memo, useCallback } from 'react';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './AdminProctoringViolations.module.css';

const getStatusBadge = (status) => {
  const statusMap = {
    clear: { label: 'Clear', icon: FiCheckCircle, color: 'green' },
    flagged: { label: 'Flagged', icon: FiAlertTriangle, color: 'red' }
  };
  return statusMap[status] || { label: status, icon: FiAlertTriangle, color: 'gray' };
};

const ViolationCardMemo = memo(function ViolationCard({ v, navigate, styles }) {
  const statusBadge = getStatusBadge(v.status);
  const StatusIcon = statusBadge.icon;
  const startDate = new Date(v.assessmentStartedAt).toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric'
  });
  const startTime = new Date(v.assessmentStartedAt).toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit'
  });

  return (
    <div 
      className={styles.violationCard}
      onClick={() => navigate(`/admin/proctoring-violations/${v.candidate._id}`)}
    >
      <div className={styles.cardHeader}>
        <div className={styles.candidateInfo}>
          <div className={styles.candidateAvatar}>{v.candidate?.fullName?.[0]}</div>
          <div className={styles.candidateDetails}>
            <div className={styles.candidateName}>{v.candidate?.fullName}</div>
            <div className={styles.candidateEmail}>{v.candidate?.email}</div>
          </div>
        </div>
        <span className={`${styles.statusBadge} ${styles[`status${statusBadge.color}`]}`}>
          <span className={styles.badgeIcon}><StatusIcon aria-hidden="true" /></span>
          {statusBadge.label}
        </span>
      </div>
      
      <div className={styles.cardBody}>
        <div className={styles.metricItem}>
          <span className={styles.metricLabel}>Role</span>
          <span className={styles.metricValue}>{v.candidate?.role}</span>
        </div>
        <div className={styles.metricItem}>
          <span className={styles.metricLabel}>Violations</span>
          <span className={styles.metricValueHighlight}>{v.violationCount}</span>
        </div>
        <div className={styles.metricItem}>
          <span className={styles.metricLabel}>Started</span>
          <span className={styles.metricValue}>{startDate} {startTime}</span>
        </div>
      </div>
      
      <div className={styles.cardFooter}>
        <button className={styles.viewBtn}>View Details →</button>
      </div>
    </div>
  );
});

export default function AdminProctoringViolations() {
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { refreshKey } = useNavRefresh();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get('/admin/proctoring-violations');
        setViolations(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refreshKey]);

  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.page}>
          <div className={styles.loading}>Loading violations...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h2 className={styles.title}>Proctoring Violations</h2>
          <p className={styles.subtitle}>Monitor candidates who violated assessment rules</p>
        </div>

        {violations.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><FiCheckCircle aria-hidden="true" /></div>
            <h3 className={styles.emptyTitle}>No Violations Detected</h3>
            <p className={styles.emptyText}>All candidates have followed the proctoring guidelines.</p>
          </div>
        ) : (
          <div className={styles.cardsList}>
            {violations.map((v) => (
              <ViolationCardMemo key={v._id} v={v} navigate={navigate} styles={styles} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
